import React, { useState } from 'react';
import { useInfluencerStore } from '@/stores/influencerStore';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  MousePointer, 
  Globe, 
  Clock, 
  Target,
  AlertCircle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

// Mock data for comprehensive analytics
const mockAnalyticsData = {
  // Visitor metrics
  visitorMetrics: {
    totalVisitors: 1247,
    uniqueVisitors: 892,
    returnVisitors: 355,
    newVisitors: 537,
    bounceRate: 23.4,
    avgSessionDuration: '2m 34s',
    pagesPerSession: 3.2
  },

  // Click tracking over time
  clickTrends: [
    { date: '2024-01-01', clicks: 45, uniqueClicks: 38, conversions: 12 },
    { date: '2024-01-02', clicks: 52, uniqueClicks: 44, conversions: 15 },
    { date: '2024-01-03', clicks: 38, uniqueClicks: 32, conversions: 8 },
    { date: '2024-01-04', clicks: 67, uniqueClicks: 58, conversions: 22 },
    { date: '2024-01-05', clicks: 89, uniqueClicks: 76, conversions: 31 },
    { date: '2024-01-06', clicks: 76, uniqueClicks: 65, conversions: 24 },
    { date: '2024-01-07', clicks: 94, uniqueClicks: 82, conversions: 35 },
    { date: '2024-01-08', clicks: 112, uniqueClicks: 98, conversions: 42 },
    { date: '2024-01-09', clicks: 98, uniqueClicks: 87, conversions: 33 },
    { date: '2024-01-10', clicks: 134, uniqueClicks: 118, conversions: 51 },
    { date: '2024-01-11', clicks: 156, uniqueClicks: 142, conversions: 67 },
    { date: '2024-01-12', clicks: 178, uniqueClicks: 165, conversions: 78 },
    { date: '2024-01-13', clicks: 145, uniqueClicks: 134, conversions: 62 },
    { date: '2024-01-14', clicks: 167, uniqueClicks: 156, conversions: 73 }
  ],

  // Geographic data
  geographicData: [
    { country: 'United States', visitors: 456, percentage: 36.6 },
    { country: 'United Kingdom', visitors: 234, percentage: 18.8 },
    { country: 'Canada', visitors: 189, percentage: 15.2 },
    { country: 'Australia', visitors: 156, percentage: 12.5 },
    { country: 'Germany', visitors: 98, percentage: 7.9 },
    { country: 'France', visitors: 67, percentage: 5.4 },
    { country: 'Other', visitors: 47, percentage: 3.8 }
  ],

  // Traffic sources
  trafficSources: [
    { source: 'Instagram', visitors: 567, percentage: 45.5 },
    { source: 'TikTok', visitors: 234, percentage: 18.8 },
    { source: 'Twitter', visitors: 189, percentage: 15.2 },
    { source: 'Facebook', visitors: 156, percentage: 12.5 },
    { source: 'Direct', visitors: 67, percentage: 5.4 },
    { source: 'Other', visitors: 34, percentage: 2.7 }
  ],

  // Device breakdown
  deviceData: [
    { device: 'Mobile', visitors: 892, percentage: 71.5 },
    { device: 'Desktop', visitors: 298, percentage: 23.9 },
    { device: 'Tablet', visitors: 57, percentage: 4.6 }
  ],

  // Hourly activity
  hourlyActivity: [
    { hour: '00:00', clicks: 12 }, { hour: '01:00', clicks: 8 }, { hour: '02:00', clicks: 5 },
    { hour: '03:00', clicks: 3 }, { hour: '04:00', clicks: 2 }, { hour: '05:00', clicks: 4 },
    { hour: '06:00', clicks: 7 }, { hour: '07:00', clicks: 15 }, { hour: '08:00', clicks: 28 },
    { hour: '09:00', clicks: 45 }, { hour: '10:00', clicks: 67 }, { hour: '11:00', clicks: 89 },
    { hour: '12:00', clicks: 112 }, { hour: '13:00', clicks: 98 }, { hour: '14:00', clicks: 76 },
    { hour: '15:00', clicks: 89 }, { hour: '16:00', clicks: 134 }, { hour: '17:00', clicks: 156 },
    { hour: '18:00', clicks: 178 }, { hour: '19:00', clicks: 145 }, { hour: '20:00', clicks: 167 },
    { hour: '21:00', clicks: 134 }, { hour: '22:00', clicks: 89 }, { hour: '23:00', clicks: 45 }
  ],

  // Performance insights
  insights: [
    {
      type: 'positive',
      title: 'Strong Weekend Performance',
      description: 'Your link performs 34% better on weekends. Consider timing your posts accordingly.',
      metric: '+34%',
      icon: TrendingUp
    },
    {
      type: 'positive',
      title: 'Mobile Optimization Working',
      description: '71.5% of your traffic comes from mobile devices, showing good mobile reach.',
      metric: '71.5%',
      icon: Users
    },
    {
      type: 'warning',
      title: 'Bounce Rate Above Average',
      description: 'Your bounce rate of 23.4% is higher than the industry average of 18%.',
      metric: '23.4%',
      icon: AlertCircle
    },
    {
      type: 'positive',
      title: 'Instagram is Your Top Source',
      description: '45.5% of your traffic comes from Instagram. Focus on this platform.',
      metric: '45.5%',
      icon: CheckCircle
    }
  ],

  // Recommendations
  recommendations: [
    {
      priority: 'high',
      title: 'Optimize for Mobile',
      description: 'With 71.5% mobile traffic, ensure your landing page is mobile-first.',
      impact: 'High',
      effort: 'Medium'
    },
    {
      priority: 'medium',
      title: 'Post During Peak Hours',
      description: 'Peak activity is 6-8 PM. Schedule posts during these hours.',
      impact: 'Medium',
      effort: 'Low'
    },
    {
      priority: 'high',
      title: 'Improve Landing Page',
      description: 'Reduce bounce rate by improving page load speed and content.',
      impact: 'High',
      effort: 'High'
    },
    {
      priority: 'medium',
      title: 'Expand TikTok Presence',
      description: 'TikTok drives 18.8% of traffic. Consider more TikTok content.',
      impact: 'Medium',
      effort: 'Medium'
    }
  ]
};

const COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#84CC16'];

export const PerformanceAnalytics: React.FC = () => {
  const { dashboardData } = useInfluencerStore();
  const [timeRange, setTimeRange] = useState('14d');

  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No analytics data available</p>
      </div>
    );
  }

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Header with time range selector */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Performance Analytics</h3>
          <p className="text-sm text-gray-500">Track your referral link performance and visitor insights</p>
        </div>
        <select 
          value={timeRange} 
          onChange={(e) => setTimeRange(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="14d">Last 14 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </div>

      {/* Visitor Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Visitors</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(mockAnalyticsData.visitorMetrics.totalVisitors)}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <ArrowUpRight className="h-4 w-4 text-green-500" />
            <span className="text-green-600 ml-1">+12.5%</span>
            <span className="text-gray-500 ml-1">vs last period</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MousePointer className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Unique Visitors</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(mockAnalyticsData.visitorMetrics.uniqueVisitors)}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <ArrowUpRight className="h-4 w-4 text-green-500" />
            <span className="text-green-600 ml-1">+8.3%</span>
            <span className="text-gray-500 ml-1">vs last period</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(mockAnalyticsData.visitorMetrics.bounceRate)}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <ArrowDownRight className="h-4 w-4 text-red-500" />
            <span className="text-red-600 ml-1">-2.1%</span>
            <span className="text-gray-500 ml-1">vs last period</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg. Session</p>
              <p className="text-2xl font-bold text-gray-900">{mockAnalyticsData.visitorMetrics.avgSessionDuration}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <ArrowUpRight className="h-4 w-4 text-green-500" />
            <span className="text-green-600 ml-1">+15.2%</span>
            <span className="text-gray-500 ml-1">vs last period</span>
          </div>
        </div>
      </div>

      {/* Click Trends Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Click Trends</h4>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={mockAnalyticsData.clickTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            />
            <YAxis />
            <Tooltip 
              labelFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric' 
              })}
              formatter={(value: any, name: string) => [
                value, 
                name === 'clicks' ? 'Total Clicks' : 
                name === 'uniqueClicks' ? 'Unique Clicks' : 'Conversions'
              ]}
            />
            <Area 
              type="monotone" 
              dataKey="clicks" 
              stackId="1" 
              stroke="#3B82F6" 
              fill="#3B82F6" 
              fillOpacity={0.6}
            />
            <Area 
              type="monotone" 
              dataKey="uniqueClicks" 
              stackId="2" 
              stroke="#8B5CF6" 
              fill="#8B5CF6" 
              fillOpacity={0.6}
            />
            <Area 
              type="monotone" 
              dataKey="conversions" 
              stackId="3" 
              stroke="#10B981" 
              fill="#10B981" 
              fillOpacity={0.6}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Geographic and Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic Distribution */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Geographic Distribution</h4>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={mockAnalyticsData.geographicData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ country, percentage }) => `${country} (${percentage}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="visitors"
              >
                {mockAnalyticsData.geographicData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any) => [formatNumber(value), 'Visitors']} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Traffic Sources</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mockAnalyticsData.trafficSources}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="source" />
              <YAxis />
              <Tooltip formatter={(value: any) => [formatNumber(value), 'Visitors']} />
              <Bar dataKey="visitors" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Device Breakdown and Hourly Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Device Breakdown</h4>
          <div className="space-y-4">
            {mockAnalyticsData.deviceData.map((device, index) => (
              <div key={device.device} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-full mr-3"
                    style={{ backgroundColor: COLORS[index] }}
                  />
                  <span className="text-sm font-medium text-gray-700">{device.device}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full"
                      style={{ 
                        width: `${device.percentage}%`, 
                        backgroundColor: COLORS[index] 
                      }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-12">{device.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hourly Activity */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Hourly Activity</h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mockAnalyticsData.hourlyActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip formatter={(value: any) => [value, 'Clicks']} />
              <Line 
                type="monotone" 
                dataKey="clicks" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights and Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Key Insights */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Key Insights</h4>
          <div className="space-y-4">
            {mockAnalyticsData.insights.map((insight, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 rounded-lg border">
                <div className={`p-2 rounded-lg ${
                  insight.type === 'positive' ? 'bg-green-100' : 
                  insight.type === 'warning' ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  <insight.icon className={`h-5 w-5 ${
                    insight.type === 'positive' ? 'text-green-600' : 
                    insight.type === 'warning' ? 'text-yellow-600' : 'text-red-600'
                  }`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-medium text-gray-900">{insight.title}</h5>
                    <span className={`text-sm font-bold ${
                      insight.type === 'positive' ? 'text-green-600' : 
                      insight.type === 'warning' ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {insight.metric}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{insight.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Recommendations */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Performance Recommendations</h4>
          <div className="space-y-4">
            {mockAnalyticsData.recommendations.map((rec, index) => (
              <div key={index} className="p-4 rounded-lg border">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                        rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {rec.priority.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-500">Impact: {rec.impact}</span>
                      <span className="text-sm text-gray-500">Effort: {rec.effort}</span>
                    </div>
                    <h5 className="text-sm font-medium text-gray-900 mb-1">{rec.title}</h5>
                    <p className="text-sm text-gray-600">{rec.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}; 